/**
 * 본인인증 테스트 페이지
 * Mobile-OK (휴대폰 인증) + EZ-IOK (간편인증) 테스트용
 *
 * 라우트: /auth-test
 */

import { useState } from 'react';
import { useMobileOK } from '../../hooks/useMobileOK';
import { useEZIOK } from '../../hooks/useEZIOK';
import UnifiedAuthModal from '../../components/UnifiedAuthModal/UnifiedAuthModal';

// 간편인증 제공자 목록
const EZIOK_PROVIDERS = [
  { code: 'kakao', name: '카카오' },
  { code: 'naver', name: '네이버' },
  { code: 'passauth', name: 'PASS' },
  { code: 'toss', name: '토스' },
  { code: 'shinhan', name: '신한은행' },
  { code: 'kbstar', name: 'KB국민은행' },
  { code: 'kebhana', name: '하나은행' },
  { code: 'woori', name: '우리은행' },
  { code: 'nhlife', name: 'NH농협' },
  { code: 'kakaobank', name: '카카오뱅크' },
  { code: 'banksalad', name: '뱅크샐러드' },
  { code: 'signgate', name: '삼성패스' },
  { code: 'yeskey', name: '금융결제원' },
];

const AuthTest: React.FC = () => {
  // 상태
  const [logs, setLogs] = useState<string[]>([]);
  const [result, setResult] = useState<any>(null);
  const [selectedProvider, setSelectedProvider] = useState('kakao');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 로그 추가 함수
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [`[${timestamp}] ${message}`, ...prev]);
    console.log(`[AuthTest] ${message}`);
  };

  // Mobile-OK 훅
  const {
    startVerification: startMobileOK,
    isLoading: isMobileOKLoading,
    isSDKLoaded: isMobileOKSDKLoaded,
  } = useMobileOK({
    onSuccess: (data) => {
      addLog(`✅ Mobile-OK 성공: ${JSON.stringify(data)}`);
      setResult(data);
    },
    onError: (error) => {
      addLog(`❌ Mobile-OK 실패: ${error.message}`);
    },
  });

  // EZ-IOK 훅
  const {
    startVerification: startEZIOK,
    isLoading: isEZIOKLoading,
    isSDKLoaded: isEZIOKSDKLoaded,
  } = useEZIOK({
    onSuccess: (data) => {
      addLog(`✅ EZ-IOK 성공: ${JSON.stringify(data)}`);
      setResult(data);
    },
    onError: (error) => {
      addLog(`❌ EZ-IOK 실패: ${error.message}`);
    },
  });

  // Mobile-OK 테스트
  const handleMobileOKTest = async () => {
    addLog('🚀 Mobile-OK 인증 시작...');
    try {
      await startMobileOK({
        usageCode: '01005', // 본인확인
      });
      addLog('📱 Mobile-OK 팝업 열림');
    } catch (error) {
      addLog(`❌ Mobile-OK 시작 실패: ${error}`);
    }
  };

  // EZ-IOK 테스트
  const handleEZIOKTest = async () => {
    addLog(`🚀 EZ-IOK 인증 시작 (${selectedProvider})...`);
    try {
      await startEZIOK({
        authProviderCode: selectedProvider,
      });
      addLog('📱 EZ-IOK 팝업 열림');
    } catch (error) {
      addLog(`❌ EZ-IOK 시작 실패: ${error}`);
    }
  };

  // 통합 모달 테스트
  const handleUnifiedModalTest = () => {
    addLog('🚀 통합 인증 모달 열기...');
    setIsModalOpen(true);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>본인인증 테스트</h1>

      {/* SDK 상태 */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <h3 style={{ margin: '0 0 10px 0' }}>SDK 상태</h3>
        <div style={{ display: 'flex', gap: '20px' }}>
          <span>
            Mobile-OK:{' '}
            <strong style={{ color: isMobileOKSDKLoaded ? 'green' : 'red' }}>
              {isMobileOKSDKLoaded ? '✅ 로드됨' : '⏳ 로딩 중...'}
            </strong>
          </span>
          <span>
            EZ-IOK:{' '}
            <strong style={{ color: isEZIOKSDKLoaded ? 'green' : 'red' }}>
              {isEZIOKSDKLoaded ? '✅ 로드됨' : '⏳ 로딩 중...'}
            </strong>
          </span>
        </div>
      </div>

      {/* 테스트 버튼들 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px',
          marginBottom: '20px',
        }}
      >
        {/* Mobile-OK 테스트 */}
        <div
          style={{
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '8px',
          }}
        >
          <h3 style={{ margin: '0 0 15px 0' }}>휴대폰 인증 (Mobile-OK)</h3>
          <button
            onClick={handleMobileOKTest}
            disabled={isMobileOKLoading || !isMobileOKSDKLoaded}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isMobileOKLoading ? '#ccc' : '#4a90d9',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isMobileOKLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {isMobileOKLoading ? '인증 중...' : '휴대폰 인증 테스트'}
          </button>
        </div>

        {/* EZ-IOK 테스트 */}
        <div
          style={{
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '8px',
          }}
        >
          <h3 style={{ margin: '0 0 15px 0' }}>간편인증 (EZ-IOK)</h3>
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              marginBottom: '10px',
              borderRadius: '6px',
              border: '1px solid #ddd',
            }}
          >
            {EZIOK_PROVIDERS.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleEZIOKTest}
            disabled={isEZIOKLoading || !isEZIOKSDKLoaded}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isEZIOKLoading ? '#ccc' : '#00C853',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isEZIOKLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {isEZIOKLoading ? '인증 중...' : '간편인증 테스트'}
          </button>
        </div>
      </div>

      {/* 통합 모달 테스트 */}
      <div
        style={{
          padding: '20px',
          border: '2px solid #4a90d9',
          borderRadius: '8px',
          marginBottom: '20px',
        }}
      >
        <h3 style={{ margin: '0 0 15px 0' }}>통합 인증 모달 (UnifiedAuthModal)</h3>
        <button
          onClick={handleUnifiedModalTest}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#9C27B0',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          통합 인증 모달 열기
        </button>
      </div>

      {/* 결과 표시 */}
      {result && (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ margin: '0 0 10px 0' }}>인증 결과</h3>
          <pre
            style={{
              backgroundColor: 'white',
              padding: '12px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
            }}
          >
            {JSON.stringify(result, null, 2)}
          </pre>
          <button
            onClick={() => setResult(null)}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            결과 지우기
          </button>
        </div>
      )}

      {/* 로그 */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#263238',
          borderRadius: '8px',
          color: '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <h3 style={{ margin: 0 }}>로그</h3>
          <button
            onClick={() => setLogs([])}
            style={{
              padding: '6px 12px',
              backgroundColor: '#455a64',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            로그 지우기
          </button>
        </div>
        <div
          style={{
            height: '200px',
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          {logs.length === 0 ? (
            <div style={{ color: '#78909c' }}>로그가 없습니다.</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #37474f' }}>
                {log}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 통합 인증 모달 */}
      <UnifiedAuthModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          addLog('모달 닫힘');
        }}
        onSuccess={(result) => {
          addLog(`✅ 통합 인증 성공: ${JSON.stringify(result)}`);
          setResult(result);
          setIsModalOpen(false);
        }}
        onError={(error) => {
          addLog(`❌ 통합 인증 실패: ${error.message}`);
        }}
        purpose="general"
      />
    </div>
  );
};

export default AuthTest;